import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useInvalidate } from '@refinedev/core'
import { App } from 'antd'
import { useTranslation } from 'react-i18next'

import { ordersPatchCancellationRequestRejectionMutation } from '@repo/sdk'

import { useInvalidateFulfillmentOrders } from '../orders/useInvalidateFulfillmentOrders'

export function useRejectCancellation(onSuccess?: () => void) {
  const invalidate = useInvalidate()
  const queryClient = useQueryClient()
  const invalidateFulfillmentOrders = useInvalidateFulfillmentOrders()
  const { message } = App.useApp()
  const { t } = useTranslation()
  const [isInvalidating, setIsInvalidating] = useState(false)

  const { mutate, isPending } = useMutation({
    ...ordersPatchCancellationRequestRejectionMutation(),
    onSuccess: async (_data, variables) => {
      message.success(t('common.modals.rejectCancellationSuccess'))
      setIsInvalidating(true)
      await Promise.all([
        // `detail` only matches when the id is supplied — without it the detail stays stale.
        invalidate({
          resource: 'cancellation-list',
          invalidates: ['list', 'detail'],
          id: variables.path?.cancellationId,
        }),
        queryClient.invalidateQueries({ queryKey: ['cancellationTabCounts'] }),
        // The order/FO status in 판매내역 changes with the rejection.
        invalidateFulfillmentOrders(),
      ])
      setIsInvalidating(false)
      onSuccess?.()
    },
    onError: () => {
      message.error(t('common.modals.rejectCancellationFailed'))
    },
  })

  const rejectCancellation = (cancellationId: string, rejectionReason: string) => {
    mutate({ path: { cancellationId }, body: { rejectionReason } })
  }

  return { rejectCancellation, isRejecting: isPending || isInvalidating }
}
